import React, { Fragment, useEffect, useContext } from 'react'
import PropTypes from 'prop-types'
import BEMHelper from '@ynap/bem-helper'
import { Route, Link } from 'react-router-dom'
import { Button } from '@ynap/button'
import { LineItem } from '@ynap/line-item'
import MediaQuery from '@ynap/media-query'
import ReturnsAndExchangesForm from '../../ReturnsAndExchangesForm/ReturnsAndExchangesForm'
import Overlay from '@ynap/overlay'
import { LiteLayout } from '@ynap/product-details-page'
import { useConfig } from '@ynap/immutables-provider'
import track, { TrackingPropType } from 'react-tracking'
import {
    trackCreateReturnExchangeItem,
    trackCreateReturnContinueExchange,
    trackCreateReturnCancelExchange,
} from '../analytics'
import { processBadges } from '../Returns/utils'
import { NavContext } from '../../MyAccountNav/MyAccountNavWrapper/MyAccountNavWrapper'

const bem = new BEMHelper('CreateReturn')

const propTypes = {
    messages: PropTypes.shape({
        orderDetails: PropTypes.shape({
            title: PropTypes.func.isRequired,
            orderInfo: PropTypes.func.isRequired,
            trackOrder: PropTypes.func.isRequired,
            dateShipped: PropTypes.func.isRequired,
            returnInfo: PropTypes.func,
            pageError: PropTypes.func.isRequired,
            retryButton: PropTypes.func.isRequired,
        }),
    }),
    clearReturn: PropTypes.func.isRequired,
    clearAllReturns: PropTypes.func.isRequired,
    addReturn: PropTypes.func.isRequired,
    selectReturnCode: PropTypes.func.isRequired,
    selectProductColour: PropTypes.func.isRequired,
    externalOrderId: PropTypes.string,
    returnOrder: PropTypes.object.isRequired,
    returnItems: PropTypes.array.isRequired,
    orderItems: PropTypes.arrayOf(
        PropTypes.shape({
            partNumber: PropTypes.string,
            image: PropTypes.string,
            brand: PropTypes.string,
            name: PropTypes.string,
            colour: PropTypes.string,
            size: PropTypes.string,
            quantity: PropTypes.string,
            price: PropTypes.object,
        })
    ),
    locale: PropTypes.object.isRequired,
    isExchangable: PropTypes.bool,
    tracking: TrackingPropType,
}

function CreateReturn({
    messages,
    match,
    clearReturn,
    clearAllReturns,
    addReturn,
    push,
    selectReturnCode,
    selectReturnOrExchange,
    selectProductColour,
    externalOrderId,
    returnOrder,
    locale,
    orderItems,
    returnItems,
    sizeGridFeatureToggle,
    isExchangable,
    urlPath,
    tracking,
}) {
    const {
        head: {
            flags: { showStatusAsBadge = false, ctaExchangeReturnToBtn } = {},
            deliveryTrackingService: { enabled: deliveryTrackingEnabled },
        },
    } = useConfig()

    const { setNavigation } = useContext(NavContext) || {}

    useEffect(() => {
        setNavigation?.({
            preTitle: messages?.pages?.orderdetails?.title?.(),
            title: messages?.pages?.orderdetailsReturn?.title?.(),
        })
    }, [])

    const renderExchangeReturnCTA = () => {
        if (isExchangable && ctaExchangeReturnToBtn) {
            return <Button type={'secondary'}>{messages.orderDetails.exchangeReturn()}</Button>
        } else if (isExchangable && !ctaExchangeReturnToBtn) {
            return messages.orderDetails.exchangeReturn()
        } else {
            return messages.orderDetails.return()
        }
    }

    return (
        <div className={bem(null)}>
            <h2 className={bem('returnTitle')}>
                {isExchangable ? messages.orderDetails.exchangeTitle() : messages.orderDetails.returnTitle()}
            </h2>
            <ul className={bem('items')}>
                {orderItems.map((item, index) => {
                    const { isItemReturnable, primeLineNo } = item

                    const itemReturnLine =
                        returnOrder && returnOrder.returnLines.find((line) => line.lineNumber === primeLineNo)

                    const returnStatus = messages.orderDetails.returnStatus({
                        type: itemReturnLine ? itemReturnLine.returnOrExchange : '',
                    })
                    const isInReturnItems =
                        isItemReturnable &&
                        !!returnItems?.find(({ primeLineNo: returnItemPLN }) => returnItemPLN === primeLineNo)

                    const processedBadges = processBadges(item, showStatusAsBadge, isInReturnItems, returnStatus)
                    const { amount, divisor } = item.price.value

                    const MiniPDP = (withQuickAdd) => (
                        <LiteLayout
                            product={{
                                partNumber: item.partNumber,
                                designerName: item.brand,
                                name: item.name,
                            }}
                            selectedProductVariant={{
                                price: {
                                    ...item.price,
                                    finalPrice: amount / divisor,
                                }, // remove all non-numeric characters and whitespace apart from .
                                images: item.images,
                            }}
                            messages={messages}
                            locale={locale.id}
                            withQuickAdd={withQuickAdd}
                        >
                            <ReturnsAndExchangesForm
                                className={bem('returnExchangeForm')}
                                messages={messages}
                                onSubmit={(values) => addReturn(externalOrderId, item, values, urlPath)}
                                returnCodes={item.returnCodes}
                                colours={item.colours}
                                skus={item.skus}
                                selectProductColour={selectProductColour}
                                selectReturnCode={selectReturnCode}
                                selectedProductColour={item.selectedProductColour}
                                selectedReturnCode={item.selectedReturnCode}
                                selectReturnOrExchange={selectReturnOrExchange}
                                showCommentsBox={item.showCommentsBox}
                                lineNumber={item.primeLineNo}
                                returnOrExchange={item.returnOrExchange}
                                initialReturnValues={itemReturnLine}
                                soldOut={item.soldOut}
                                sizeGridFeatureToggle={sizeGridFeatureToggle}
                                exchangable={item.exchangable}
                                urlPath={urlPath}
                                addReturn={addReturn}
                                externalOrderId={externalOrderId}
                                item={item}
                                locale={locale}
                                coloursWithSkus={item.coloursWithSkus}
                            />
                        </LiteLayout>
                    )

                    return (
                        <LineItem
                            key={`${item.partNumber}-${index}`}
                            {...item}
                            badges={processedBadges}
                            messages={messages}
                        >
                            {item.showOrderItemStatus && (
                                <p className={bem('status')}>
                                    {deliveryTrackingEnabled
                                        ? messages.orderItemStatus?.[item.activeOrderItemStatus]?.()
                                        : item.orderItemStatus}
                                </p>
                            )}

                            {isItemReturnable &&
                                (isInReturnItems ? (
                                    <Fragment>
                                        {!showStatusAsBadge && (
                                            <div className={bem('returnStatus')}>{returnStatus}</div>
                                        )}
                                        <div className={bem('returnActions')}>
                                            <Button
                                                className={bem('addReturn')}
                                                component={Link}
                                                to={`${match.url}/${item.primeLineNo}`}
                                            >
                                                {messages.orderDetails.edit()}
                                            </Button>
                                            <Button className={bem('clearReturn')} onClick={() => clearReturn(item)}>
                                                {messages.orderDetails.cancel()}
                                            </Button>
                                        </div>
                                    </Fragment>
                                ) : (
                                    <Fragment>
                                        {item.soldOut ? (
                                            <div className={bem('soldOut')}>{messages.orderDetails.soldOut()}</div>
                                        ) : null}
                                        <div className={bem('returnActions')}>
                                            <Link
                                                className={bem('exchangeReturnLink')}
                                                to={`${match.url}/${item.primeLineNo}`}
                                                onClick={() => {
                                                    tracking.trackEvent(
                                                        trackCreateReturnExchangeItem(item, externalOrderId, locale)
                                                    )
                                                }}
                                            >
                                                {renderExchangeReturnCTA()}
                                            </Link>
                                        </div>
                                    </Fragment>
                                ))}

                            <Route
                                path={`/:locale/account/${urlPath}/:externalOrderId/return/${item.primeLineNo}`}
                                render={() => {
                                    return (
                                        <Overlay
                                            onClose={() =>
                                                push(`/${locale.id}/account/${urlPath}/${externalOrderId}/return`)
                                            }
                                            className={{
                                                content: bem('overlay'),
                                            }}
                                        >
                                            <div className={bem('litePdpWithForm')}>
                                                <MediaQuery
                                                    screens={{
                                                        desktop: () => MiniPDP(),
                                                        phone: () => MiniPDP('withQuickAdd'),
                                                        tablet: () => MiniPDP('withQuickAdd'),
                                                    }}
                                                />
                                            </div>
                                        </Overlay>
                                    )
                                }}
                            />
                        </LineItem>
                    )
                })}
            </ul>
            <Button
                component={Link}
                type={returnOrder ? 'primary' : null}
                to={`/${locale.id}/account/${urlPath}/${externalOrderId}/return/review`}
                className={bem('toReview', !returnOrder ? 'disabled' : null)}
                onClick={
                    !returnOrder
                        ? (e) => e.preventDefault()
                        : () => {
                              tracking.trackEvent(
                                  trackCreateReturnContinueExchange(returnItems, externalOrderId, locale)
                              )
                          }
                }
            >
                {messages.orderDetails.review()}
            </Button>
            <Button
                component={Link}
                type={'primary-link'}
                to={`/${locale.id}/account/${urlPath}/${externalOrderId}`}
                onClick={() => {
                    tracking.trackEvent(trackCreateReturnCancelExchange(externalOrderId, locale))
                    clearAllReturns()
                }}
                className={bem('cancelReturn')}
            >
                {messages.orderDetails.cancel()}
            </Button>
        </div>
    )
}

CreateReturn.propTypes = propTypes

export default track()(CreateReturn)
